import { useState, type FormEvent } from "react";
import { CRYPTO_NETWORKS, deliveryErrors, MIN_ORDER_MAX_ARS, parseMinOrder } from "../../shared/market";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError } from "../lib/api";
import { actingShop } from "../lib/actAs";
import { marketApi, type MySeller } from "./api";
import { DeliveryFieldset, deliveryPayload, draftFrom, type DeliveryDraft } from "./DeliverySettings";

/**
 * Shop settings: profile, how buyers pay (Mercado Pago, transfer, crypto), delivery, discounts.
 * The admin edits a seller's with `save` (its own endpoint) and `admin`: Mercado Pago is the seller's
 * own connection, so there it only shows whether it's connected.
 */
export function SellerSettings({ seller, onSaved, save = marketApi.saveSeller, admin = false }: { seller: MySeller; onSaved: (s: MySeller) => void; save?: (b: Record<string, unknown>) => Promise<MySeller>; admin?: boolean }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error" | "blocked">("idle");
  const [mpBusy, setMpBusy] = useState(false);
  const d = seller.delivery;
  const [draft, setDraft] = useState<DeliveryDraft>(() => draftFrom(d));
  const [deliveryErrs, setDeliveryErrors] = useState<string[]>([]);
  const [walletError, setWalletError] = useState(false);
  const [minOrderError, setMinOrderError] = useState(false);
  const wallet = seller.wallets[0];
  // The admin working from inside the shop: where the money goes is the owner's to set.
  const acting = !admin && actingShop() !== null;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const s = (k: string) => String(f.get(k) ?? "").trim();
    const on = (k: string) => f.get(k) === "on";
    // In-person options need their details: say so next to each one instead of saving half of it.
    const delivery = deliveryPayload(draft);
    const bad = deliveryErrors(delivery);
    if (bad.length) {
      setDeliveryErrors(bad);
      setStatus("error");
      return;
    }
    // Whole pesos; empty (or 0) is no minimum.
    const minOrder = parseMinOrder(s("minOrderArs"));
    if (minOrder === undefined) {
      setMinOrderError(true);
      setStatus("error");
      return;
    }
    setStatus("saving");
    setWalletError(false);
    setMinOrderError(false);
    try {
      onSaved(
        await save({
          displayName: s("displayName"),
          bio: s("bio"),
          contactPhone: s("contactPhone"),
          city: s("city"),
          province: s("province"),
          vacationUntil: s("vacationUntil") || null,
          transfer: s("holder") && (s("cbu") || s("alias")) ? { holder: s("holder"), taxId: s("taxId"), bank: s("bank"), cbu: s("cbu"), alias: s("alias") } : null,
          wallets: s("walletAddress") ? [{ asset: "USDT", network: s("walletNetwork"), address: s("walletAddress") }] : [],
          delivery,
          acceptsOffers: on("acceptsOffers"),
          acceptsCash: on("acceptsCash"),
          minOrderArs: minOrder,
        }),
      );
      setStatus("saved");
    } catch (err) {
      const fields = err instanceof ApiError ? ((err.details as { fields?: string[] })?.fields ?? []) : [];
      setDeliveryErrors(fields.filter((x) => x.startsWith("delivery.")));
      setWalletError(fields.includes("wallets"));
      setMinOrderError(fields.includes("minOrderArs"));
      setStatus(err instanceof ApiError && err.code === "blocked" ? "blocked" : "error");
    }
  };

  return (
    <form className="stack seller-settings" onSubmit={submit}>
      <fieldset className="stack">
        <legend>{t("mkt.set.profile")}</legend>
        <div className="field">
          <label htmlFor="ss-name">{t("mkt.shopName")}</label>
          <input id="ss-name" name="displayName" defaultValue={seller.displayName} maxLength={40} required />
        </div>
        <div className="field">
          <label htmlFor="ss-bio">{t("mkt.bio")}</label>
          <textarea id="ss-bio" name="bio" rows={3} maxLength={600} defaultValue={seller.bio} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ss-phone">{t("mkt.contactPhone")}</label>
            <input id="ss-phone" name="contactPhone" type="tel" defaultValue={seller.contactPhone} required />
          </div>
          <div className="field">
            <label htmlFor="ss-city">{t("mkt.city")}</label>
            <input id="ss-city" name="city" defaultValue={seller.city} />
          </div>
          <div className="field">
            <label htmlFor="ss-prov">{t("mkt.province")}</label>
            <input id="ss-prov" name="province" defaultValue={seller.province} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="ss-vac">{t("mkt.vacation")}</label>
          <input id="ss-vac" name="vacationUntil" type="date" defaultValue={seller.vacationUntil ?? ""} />
          <span className="field-hint">{t("mkt.vacationHint")}</span>
        </div>
      </fieldset>

      <fieldset className="stack">
        <legend>{t("mkt.set.payments")}</legend>
        <p className="muted small">{t("mkt.set.paymentsIntro")}</p>
        {seller.limited && <p className="notice notice-warn">🤝 {t("mkt.set.limited")}</p>}
        {acting ? (
          <p className="notice notice-warn">{t("mkt.set.ownerPays")}</p>
        ) : (
          <>
            <div className="mp-box">
              <strong>Mercado Pago</strong>
              {!seller.mp.available ? (
                <p className="muted small">{t("mkt.mpSoon")}</p>
              ) : admin ? (
                <p>{seller.mp.connected ? `✅ ${t("mkt.mpConnectedShort")}` : t("mkt.mpNotConnected")}</p>
              ) : seller.mp.connected ? (
                <p>
                  ✅ {t("mkt.mpConnectedShort")}{" "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    disabled={mpBusy}
                    onClick={async () => {
                      setMpBusy(true);
                      onSaved(await marketApi.mpDisconnect().finally(() => setMpBusy(false)));
                    }}
                  >
                    {t("mkt.mpDisconnect")}
                  </button>
                </p>
              ) : (
                <>
                  <p className="muted small">{t("mkt.mpIntro")}</p>
                  <button
                    type="button"
                    className="btn btn-outline"
                    disabled={mpBusy}
                    onClick={async () => {
                      setMpBusy(true);
                      try {
                        window.location.assign((await marketApi.mpConnect()).url);
                      } catch {
                        setMpBusy(false);
                        setStatus("error");
                      }
                    }}
                  >
                    {t("mkt.mpConnect")}
                  </button>
                </>
              )}
            </div>
            <h3 className="small-title">{t("mkt.set.transfer")}</h3>
            <div className="field-row">
              <div className="field">
                <label htmlFor="ss-holder">{t("mkt.holder")}</label>
                <input id="ss-holder" name="holder" defaultValue={seller.transfer?.holder ?? ""} />
              </div>
              <div className="field">
                <label htmlFor="ss-tax">CUIT / CUIL</label>
                <input id="ss-tax" name="taxId" defaultValue={seller.transfer?.taxId ?? ""} />
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="ss-cbu">CBU / CVU</label>
                <input id="ss-cbu" name="cbu" inputMode="numeric" defaultValue={seller.transfer?.cbu ?? ""} />
              </div>
              <div className="field">
                <label htmlFor="ss-alias">Alias</label>
                <input id="ss-alias" name="alias" defaultValue={seller.transfer?.alias ?? ""} />
              </div>
              <div className="field">
                <label htmlFor="ss-bank">{t("mkt.bank")}</label>
                <input id="ss-bank" name="bank" defaultValue={seller.transfer?.bank ?? ""} />
              </div>
            </div>
            <h3 className="small-title">{t("mkt.set.crypto")}</h3>
            <div className="field-row">
              <div className="field">
                <label htmlFor="ss-net">{t("mkt.network")}</label>
                <select id="ss-net" name="walletNetwork" defaultValue={wallet?.network ?? "TRON (TRC-20)"}>
                  {CRYPTO_NETWORKS.map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="field grow">
                <label htmlFor="ss-wallet">{t("mkt.walletAddress")}</label>
                <input
                  id="ss-wallet"
                  name="walletAddress"
                  defaultValue={wallet?.address ?? ""}
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-invalid={walletError || undefined}
                  aria-describedby={walletError ? "ss-wallet-err" : undefined}
                />
                {walletError && (
                  <span id="ss-wallet-err" className="field-error">
                    {t("mkt.walletInvalid")}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </fieldset>

      <DeliveryFieldset value={draft} onChange={(v) => (setDraft(v), setDeliveryErrors([]))} errors={deliveryErrs} />

      <fieldset className="stack">
        <legend>{t("mkt.set.sales")}</legend>
        <label className="check">
          <input type="checkbox" name="acceptsOffers" defaultChecked={seller.acceptsOffers} /> {t("mkt.set.acceptsOffers")}
        </label>
        <label className="check">
          <input type="checkbox" name="acceptsCash" defaultChecked={seller.acceptsCash} /> {t("mkt.set.cash")}
        </label>
        <p className="muted small">{t("mkt.set.cashHint")}</p>
        <div className="field">
          <label htmlFor="ss-min">{t("mkt.set.minOrder")}</label>
          <input
            id="ss-min"
            name="minOrderArs"
            type="number"
            inputMode="numeric"
            min={0}
            max={MIN_ORDER_MAX_ARS}
            step={1}
            placeholder={t("mkt.set.minOrderNone")}
            defaultValue={seller.minOrderArs ?? ""}
            aria-invalid={minOrderError || undefined}
            aria-describedby={minOrderError ? "ss-min-err" : "ss-min-hint"}
          />
          {minOrderError ? (
            <span id="ss-min-err" className="field-error">
              {t("mkt.set.minOrderInvalid")}
            </span>
          ) : (
            <span id="ss-min-hint" className="field-hint">
              {t("mkt.set.minOrderHint")}
            </span>
          )}
        </div>
      </fieldset>

      {status === "saved" && <p className="notice" role="status">{t("mkt.saved")}</p>}
      {status === "error" && <p className="form-error" role="alert">{t("mkt.settingsError")}</p>}
      {status === "blocked" && <p className="form-error" role="alert">{t("mkt.blockedBank")}</p>}
      <button type="submit" className="btn btn-primary" disabled={status === "saving"}>
        {t("mkt.saveSettings")}
      </button>
    </form>
  );
}
